import { api } from "@/lib/apiClient";
import {
  getHeroAddresses,
  getHeroBranches,
  getHeroContacts,
  getHeroCustomersByCode,
  getHeroEmployees,
  getHeroMeasures,
  getHeroProjectSteps,
  getHeroProjectTypes,
  listOrdner,
  listSOrdner,
} from "@/lib/projects";
import type {
  HeroAddressLookup,
  HeroBranchLookup,
  HeroContactLookup,
  HeroCustomerLookup,
  HeroEmployeeLookup,
  HeroMeasureLookup,
  HeroProjectStepLookup,
  HeroProjectTypeLookup,
  OrdnerOut,
  SOrdnerOut,
} from "@/lib/projects";
import {
  listGeschaeftspartnerAnsprechpartner,
  listGeschaeftspartnerObjektadressen,
  listKontaktKunden,
} from "@/lib/kontakte";
import { getAddressAssets, type AddressAsset } from "@/lib/wartungen";
import type { AutocompleteOption } from "@/components/Autocomplete";

/**
 * Datenquellen für die Autocomplete-Felder im New-Project-Wizard.
 *
 * Ziel: Dünn gehaltene Adapter, die die Backend-Antworten in
 * `AutocompleteOption<T>` übersetzen. Filterung passiert weitgehend
 * client-seitig, damit auch ohne Backend-Anpassungen sofort gesucht werden kann.
 */

function matches(haystack: string | null | undefined, needle: string): boolean {
  if (!needle) return true;
  return String(haystack || "")
    .toLowerCase()
    .includes(needle.toLowerCase());
}

/* ─────────────── Ordner / S-Ordner ─────────────── */

export async function ordnerSource(
  query: string,
  jahr?: number,
): Promise<AutocompleteOption<OrdnerOut>[]> {
  const list = await listOrdner(jahr);
  return (list || [])
    .filter(
      (o) =>
        matches(o.nummer, query) ||
        matches(o.beschreibung, query) ||
        matches(o.kunden_name, query),
    )
    .slice(0, 50)
    .map((o) => ({
      key: String(o.id),
      label: `${o.nummer} – ${o.beschreibung || ""}`,
      description: `Kunde: ${o.kunden_name} · Jahr ${o.jahr}`,
      value: o,
    }));
}

export async function sordnerSource(
  query: string,
): Promise<AutocompleteOption<SOrdnerOut>[]> {
  const list = await listSOrdner();
  return (list || [])
    .filter(
      (s) => matches(s.nummer, query) || matches(s.beschreibung, query),
    )
    .slice(0, 50)
    .map((s) => ({
      key: String(s.id),
      label: s.nummer,
      description: s.beschreibung || undefined,
      value: s,
    }));
}

/* ─────────────── Hero-Stammdaten für die Projektanlage ─────────────── */

/** Kundencode 4 = „Verschiedene“ – Sammelgruppe ohne eigenen Kundenordner. */
export const VERSCHIEDENE_KUNDENCODE = 4;

export function appKundePasstZuKundencode(
  kundencode: number | null | undefined,
  ordnerCode: number,
): boolean {
  if (kundencode === ordnerCode) return true;
  return ordnerCode === VERSCHIEDENE_KUNDENCODE && kundencode == null;
}

export function customerSource(kundenCode: number | string | null) {
  return async (
    query: string,
  ): Promise<AutocompleteOption<HeroCustomerLookup>[]> => {
    if (kundenCode == null || kundenCode === "") return [];
    const code = Number(kundenCode);
    const isVerschiedene = code === VERSCHIEDENE_KUNDENCODE;
    const q = query.trim().toLowerCase();
    const [list, appRes] = await Promise.all([
      getHeroCustomersByCode(kundenCode).catch(() => []),
      listKontaktKunden({
        q: query.trim() || undefined,
        limit: 80,
        // Sammelordner: auch Kunden ohne Kundencode. Sonst nur der passende Code.
        kundencode: isVerschiedene ? undefined : code,
      }).catch(() => ({
        items: [],
      })),
    ]);
    const byKey = new Map<string, AutocompleteOption<HeroCustomerLookup>>();
    const push = (k: HeroCustomerLookup) => {
      const key = String(k.geschaeftspartner_id ?? k.id ?? k.name);
      if (byKey.has(key)) return;
      byKey.set(key, {
        key,
        // Kundencode bewusst nicht mehr im Label anzeigen – nur der Name.
        label: String(k.name ?? "").trim() || String(k.code ?? ""),
        value: k,
      });
    };
    for (const gp of appRes.items || []) {
      if (!appKundePasstZuKundencode(gp.kundencode, code)) continue;
      push({
        id: gp.hero_contact_id,
        code: gp.kundencode ?? VERSCHIEDENE_KUNDENCODE,
        name: gp.firmenname,
        geschaeftspartner_id: gp.id,
      });
    }
    for (const k of list || []) {
      if (!q || matches(k.name, q) || matches(String(k.code ?? ""), q)) {
        push(k);
      }
    }
    let options = [...byKey.values()];
    if (q) {
      options = options.filter(
        (opt) => matches(opt.label, q) || matches(String(opt.value.code ?? ""), q),
      );
    }
    return options.slice(0, 50);
  };
}


export async function branchSource(
  query: string,
): Promise<AutocompleteOption<HeroBranchLookup>[]> {
  const list = await getHeroBranches().catch(() => []);
  return (list || [])
    .filter((b) => matches(b.name, query))
    .slice(0, 50)
    .map((b) => ({
      key: String(b.id),
      label: b.name,
      value: b,
    }));
}

export async function measureSource(
  query: string,
): Promise<AutocompleteOption<HeroMeasureLookup>[]> {
  const list = await getHeroMeasures().catch(() => []);
  return (list || [])
    .filter((m) => matches(m.name, query) || matches(m.short, query))
    .slice(0, 50)
    .map((m) => ({
      key: String(m.id),
      label: m.name || m.short || `#${m.id}`,
      description: m.short && m.name && m.name !== m.short ? m.short : undefined,
      value: m,
    }));
}

export async function projectTypeSource(
  query: string,
): Promise<AutocompleteOption<HeroProjectTypeLookup>[]> {
  const list = await getHeroProjectTypes().catch(() => []);
  return (list || [])
    .filter((t) => matches(t.name, query))
    .slice(0, 50)
    .map((t) => ({ key: String(t.id), label: t.name, value: t }));
}

export function projectStepSource(projectTypeId: number | null) {
  return async (
    query: string,
  ): Promise<AutocompleteOption<HeroProjectStepLookup>[]> => {
    if (!projectTypeId) return [];
    const list = await getHeroProjectSteps(projectTypeId).catch(() => []);
    return (list || [])
      .filter((s) => matches(s.name, query))
      .slice(0, 50)
      .map((s) => ({ key: String(s.id), label: s.name, value: s }));
  };
}

type KundeRef =
  | number
  | Pick<HeroCustomerLookup, "id" | "geschaeftspartner_id" | "code">
  | null;

function normalizeKundeRef(
  kunde: KundeRef,
): Pick<HeroCustomerLookup, "id" | "geschaeftspartner_id" | "code"> | null {
  if (kunde == null) return null;
  if (typeof kunde === "number") {
    return Number.isFinite(kunde) ? { id: kunde } : null;
  }
  return kunde;
}

async function resolveGeschaeftspartnerId(kunde: KundeRef): Promise<number | null> {
  const ref = normalizeKundeRef(kunde);
  if (!ref) return null;
  if (ref.geschaeftspartner_id != null && Number.isFinite(Number(ref.geschaeftspartner_id))) {
    return Number(ref.geschaeftspartner_id);
  }
  const heroId = Number(ref.id);
  if (!Number.isFinite(heroId)) return null;
  try {
    const res = await listKontaktKunden({
      limit: 5,
      hero_contact_id: heroId,
    });
    const hit = (res.items || [])[0];
    return hit?.id ?? null;
  } catch {
    return null;
  }
}

function apDisplayName(vorname?: string | null, nachname?: string | null, anrede?: string | null): string {
  const name = [vorname, nachname].filter(Boolean).join(" ").trim();
  if (!name) return "Ansprechpartner";
  const a = (anrede || "").trim();
  return a ? `${a} ${name}` : name;
}

export { resolveGeschaeftspartnerId };

/** Ansprechpartner aus App-Kontakten (Fallback: Hero company-contacts). */
export function contactSource(kunde: KundeRef) {
  return async (
    query: string,
  ): Promise<AutocompleteOption<HeroContactLookup>[]> => {
    const ref = normalizeKundeRef(kunde);
    if (!ref) return [];
    const gpId = await resolveGeschaeftspartnerId(ref);
    if (gpId != null) {
      const list = await listGeschaeftspartnerAnsprechpartner(gpId).catch(() => []);
      return (list || [])
        .map((ap) => {
          const full_name = apDisplayName(ap.vorname, ap.nachname, ap.anrede);
          return {
            key: String(ap.id),
            label: full_name,
            description: ap.mail || ap.telefonnummer || undefined,
            value: { id: ap.id, full_name } satisfies HeroContactLookup,
          };
        })
        .filter((opt) => matches(opt.label, query))
        .slice(0, 50);
    }
    // Fallback ohne GP: bisheriger Hero-/Shadow-Pfad (inkl. Ansprechpartner-Fallback).
    const heroId = Number(ref.id);
    if (!Number.isFinite(heroId)) return [];
    const list = await getHeroContacts(heroId).catch(() => []);
    return (list || [])
      .filter((c) => matches(c.full_name, query))
      .slice(0, 50)
      .map((c) => ({
        key: String(c.id),
        label: c.full_name,
        value: c,
      }));
  };
}

/** Orte/Objektadressen aus App-Kontakten (Hero/Shadow + lokale Orte). */
export function addressSource(kunde: KundeRef) {
  return async (
    query: string,
  ): Promise<AutocompleteOption<HeroAddressLookup>[]> => {
    const ref = normalizeKundeRef(kunde);
    if (!ref) return [];
    const gpId = await resolveGeschaeftspartnerId(ref);
    if (gpId != null) {
      const list = await listGeschaeftspartnerObjektadressen(gpId).catch(() => []);
      return (list || [])
        .filter(
          (a) =>
            matches(a.title, query) ||
            matches(a.street, query) ||
            matches(a.city, query),
        )
        .slice(0, 50)
        .map((a) => ({
          key: String(a.address_id),
          label: a.title,
          description: [a.street, a.city].filter(Boolean).join(", ") || undefined,
          value: {
            address_id: a.address_id,
            title: a.title,
            street: a.street,
            city: a.city,
            zipcode: a.zipcode,
            maps_link: a.maps_link,
            ort_id: a.ort_id ?? null,
          } satisfies HeroAddressLookup,
        }));
    }
    const heroId = Number(ref.id);
    if (!Number.isFinite(heroId)) return [];
    const list = await getHeroAddresses(heroId).catch(() => []);
    return (list || [])
      .filter(
        (a) =>
          matches(a.title, query) ||
          matches(a.street, query) ||
          matches(a.city, query),
      )
      .slice(0, 50)
      .map((a) => ({
        key: String(a.address_id),
        label: a.title,
        description: [a.street, a.city].filter(Boolean).join(", ") || undefined,
        value: a,
      }));
  };
}

export async function employeeSource(
  query: string,
): Promise<AutocompleteOption<HeroEmployeeLookup>[]> {
  const list = await getHeroEmployees().catch(() => []);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? (list || []).filter(
        (p) =>
          matches(p.name, q) || matches(p.email, q),
      )
    : list || [];
  return filtered.slice(0, 50).map((p) => ({
    key: String(p.user_id),
    label: p.name || p.email || `User #${p.user_id}`,
    description: p.email || undefined,
    value: p,
  }));
}

export type WartungsobjektOption = {
  asset_id: number;
  external_id: string;
  name?: string | null;
  maintenance_type_key?: string | null;
  maintenance_type_name?: string | null;
  label: string;
};

function assetLabel(asset: AddressAsset): string {
  const kennung = String(asset.external_id || "").trim();
  const name = String(asset.name || "").trim();
  const typeName = String(asset.maintenance_type_name || asset.maintenance_type_key || "").trim();
  const primary = kennung || name || `Objekt #${asset.asset_id ?? asset.id ?? "?"}`;
  const parts = [primary];
  if (name && name !== primary) parts.push(name);
  if (typeName) parts.push(typeName);
  return parts.join(" · ");
}

/** Wartungsobjekte am gewählten Ort (Hero-Adresse) für die Projektanlage. */
export function assetSource(heroAddressId: number | null) {
  return async (query: string): Promise<AutocompleteOption<WartungsobjektOption>[]> => {
    if (!heroAddressId || heroAddressId <= 0) return [];
    const list = await getAddressAssets(heroAddressId).catch(() => []);
    const q = query.trim().toLowerCase();
    const filtered = q
      ? list.filter((a) => {
          const hay = [
            a.external_id,
            a.name,
            a.maintenance_type_name,
            a.maintenance_type_key,
            a.sub_location_name,
          ]
            .map((v) => String(v || "").toLowerCase())
            .join(" ");
          return hay.includes(q);
        })
      : list;
    return filtered.slice(0, 80).map((a) => {
      const assetId = Number(a.asset_id ?? a.id);
      const label = assetLabel(a);
      return {
        key: String(assetId),
        label,
        description: String(a.maintenance_type_name || a.maintenance_type_key || "").trim() || undefined,
        value: {
          asset_id: assetId,
          external_id: String(a.external_id || ""),
          name: a.name,
          maintenance_type_key: a.maintenance_type_key,
          maintenance_type_name: a.maintenance_type_name,
          label,
        },
      };
    });
  };
}

/* ─────────────── Generische Autocomplete-Quelle ─────────────── */

/**
 * Hilfsfunktion: lädt eine Liste vom Backend und mappt sie auf
 * `AutocompleteOption`. Wird genutzt, falls weitere Backend-Endpunkte (Kunden,
 * Ansprechpartner, Orte usw.) im React-Wizard direkt angebunden werden – aktuell
 * leitet der Wizard die finale Erstellung an die klassische Seite weiter.
 */
export async function genericSource<T>(
  path: string,
  query: string,
  config: {
    keyOf: (item: T) => string;
    labelOf: (item: T) => string;
    descriptionOf?: (item: T) => string | undefined;
    matchFields?: (item: T) => Array<string | null | undefined>;
    queryParam?: string;
  },
): Promise<AutocompleteOption<T>[]> {
  const params = new URLSearchParams();
  if (config.queryParam && query) params.set(config.queryParam, query);
  const url = params.toString() ? `${path}?${params.toString()}` : path;
  const list = await api.get<T[]>(url).catch(() => []);
  if (!Array.isArray(list)) return [];
  const filtered = config.queryParam
    ? list
    : list.filter((item) =>
        (config.matchFields ? config.matchFields(item) : [config.labelOf(item)])
          .some((field) => matches(field, query)),
      );
  return filtered.slice(0, 50).map((item) => ({
    key: config.keyOf(item),
    label: config.labelOf(item),
    description: config.descriptionOf?.(item),
    value: item,
  }));
}
